import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChefHat, ExternalLink, MapPin, Store, QrCode } from "lucide-react";
import { apiPublic } from "@/lib/api";

export default function OurWorkPage() {
  const [cafes, setCafes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiPublic.get("/public/cafes")
      .then(({ data }) => setCafes(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Our Portfolio</span>
        <h1 className="font-heading text-4xl sm:text-6xl font-bold text-slate-900">Cafes We Have Made For</h1>
        <p className="text-slate-600 text-base sm:text-lg">
          Explore real cafes and restaurants using Tablezy digital menus to deliver an exceptional dining experience.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-96 rounded-3xl bg-slate-200 animate-pulse" />
          ))}
        </div>
      ) : cafes.length === 0 ? (
        <div className="text-center py-20 bg-card border border-border rounded-3xl space-y-4">
          <Store className="w-12 h-12 text-slate-400 mx-auto" />
          <h3 className="font-heading text-xl font-semibold">No Cafes Found</h3>
          <p className="text-slate-500 text-sm">Check back soon as we add new client cafes!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cafes.map((cafe) => (
            <article
              key={cafe.id}
              className="group bg-card border border-border rounded-3xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col"
            >
              {/* Cover Image */}
              <div className="h-52 bg-slate-900 relative overflow-hidden">
                <img
                  src={cafe.cover_image_url || "https://images.unsplash.com/photo-1542372147193-a7aca54189cd?w=800&auto=format&fit=crop"}
                  alt={cafe.cafe_name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                
                {/* Table Badge */}
                <span className="absolute top-4 right-4 bg-slate-900/90 backdrop-blur-md text-white text-xs font-mono px-3 py-1 rounded-full border border-white/20 flex items-center gap-1.5">
                  <QrCode className="w-3.5 h-3.5 text-primary" /> {cafe.table_count} Tables
                </span>
              </div>

              {/* Body */}
              <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={cafe.logo_url || "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=200&auto=format&fit=crop"}
                      alt="Logo"
                      className="w-12 h-12 rounded-2xl object-cover border border-border shadow-sm"
                    />
                    <div>
                      <h3 className="font-heading text-xl font-bold text-slate-900 leading-tight">
                        {cafe.cafe_name}
                      </h3>
                      <p className="text-xs text-primary font-medium flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3.5 h-3.5" /> {cafe.location}
                      </p>
                    </div>
                  </div>

                  <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">
                    {cafe.description}
                  </p>
                </div>

                {/* Actions */}
                <div className="pt-3 border-t border-border flex items-center gap-3">
                  <Link
                    to={cafe.menu_url || `/menu/${cafe.slug}`}
                    className="flex-1 h-12 rounded-2xl bg-slate-900 hover:bg-primary text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
                  >
                    <ChefHat className="w-4 h-4" />
                    <span>View Menu</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* CTA Box */}
      <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-12 text-center space-y-4">
        <h2 className="font-heading text-2xl sm:text-4xl font-bold">Want Your Cafe Listed Here?</h2>
        <p className="text-slate-300 text-sm sm:text-base max-w-xl mx-auto">
          Contact our team today to get your custom QR codes and high-definition digital menu built in 48 hours.
        </p>
        <div className="pt-2">
          <Link
            to="/contact"
            className="inline-flex h-12 px-8 rounded-full bg-primary text-primary-foreground text-sm font-semibold items-center justify-center gap-2 hover:bg-primary/90 transition-all"
          >
            Request QR Menu Now
          </Link>
        </div>
      </div>
    </div>
  );
}
